import React, { useState, useRef, useEffect, useCallback } from 'react'
import FileType from 'file-type/browser'
import PropTypes from 'prop-types'
import { v4 } from 'uuid'
import { Buffer } from 'buffer'

import Button from '../Button'
import FileListItem from '../FileListItem'
import data from '../utils/file-extension-to-mime-types'

window.Buffer = window.Buffer || Buffer

const Dropzone = (props) => {
  const {
    acceptedFiles,
    deleteFunction,
    isOpen,
    maxDropsAtOnce,
    maxFileSize,
    singleUpload,
    uploadFunction
  } = props

  const inputRef = useRef(null)
  const containerRef = useRef(null)
  const inTimeoutRef = useRef(null)
  const leaveTimeoutRef = useRef(null)
  const [droppedFiles, setDroppedFiles] = useState([])
  const [clickUpload, setClickUpload] = useState(false)
  const [uniqueFileErrors, setUniqueFileErrors] = useState({})
  const [acceptedMimeTypes, setAcceptedMimeTypes] = useState(null)
  const [isOver, setIsOver] = useState(false)

  useEffect(() => {
    if (acceptedFiles) {
      const acceptedMimeTypes = acceptedFiles?.split(',').map((d) => data[d.trim()])
      setAcceptedMimeTypes(acceptedMimeTypes || null)
    }
  }, [acceptedFiles])

  useEffect(() => {
    setDroppedFiles([])
  }, [isOpen])

  useEffect(() => {
    const container = containerRef.current
    const handleResize = () => {
      if (container.scrollHeight > container.clientHeight) {
        // Add the 'pe-3' class if there is a scrollbar
        container.classList.add('pe-3')
      } else {
        // Remove the 'pe-3' class if there is no scrollbar
        container.classList.remove('pe-3')
      }
    }
    // Initial check
    handleResize()
    // Attach event listeners for resizing
    const resizeObserver = new ResizeObserver(handleResize)
    resizeObserver.observe(container)
    // Cleanup on component unmount
    return () => {
      resizeObserver.disconnect()
    }
  }, [])

  const validateMagicNumbers = (file) => {
    FileType.fromBlob(file).then((res) => {
      if (res?.mime) {
        if (acceptedFiles && !acceptedMimeTypes?.some((x) => x === res?.mime)) {
          setUniqueFileErrors((prevErrors) => ({
            ...prevErrors,
            [file.name]: 'type'
          }))
          return false
        }
        file.uniqueId = v4()
        setDroppedFiles((files) => [file, ...files])
        return true
      } else if (
        (file.type === 'text/plain' && acceptedMimeTypes?.includes('text/plain')) ||
        (file.type === 'text/csv' && acceptedMimeTypes?.includes('text/csv'))
      ) {
        file.uniqueId = v4()
        setDroppedFiles((files) => [file, ...files])
        return true
      }
      setUniqueFileErrors((prevErrors) => ({
        ...prevErrors,
        [file.name]: 'type'
      }))
      return false
    })
  }

  const handleDragOver = (event) => {
    event.preventDefault()
    setIsOver(true)

    // Clear any existing timeout
    if (inTimeoutRef.current) {
      clearTimeout(inTimeoutRef.current)
    }

    inTimeoutRef.current = setTimeout(() => {
      setIsOver(false)
    }, 300)
  }

  const handleDragLeave = useCallback((event) => {
    event.preventDefault()

    // Clear any existing timeout
    if (leaveTimeoutRef.current) {
      clearTimeout(leaveTimeoutRef.current)
    }

    leaveTimeoutRef.current = setTimeout(() => {
      setIsOver(false)
    }, 500)
  }, [])

  const handleDrop = async(event) => {
    event.preventDefault()
    setIsOver(false)

    const { files } = event.dataTransfer
    await handleFiles(files)
  }

  const handleClickUpload = (event) => {
    const uploadedFiles = Array.from(event.target.files || [])
    handleFiles(uploadedFiles)

    // Reset File Input value at the end of checks.
    if (inputRef.current) {
      inputRef.current.value = ''
    }
  }

  const handleFiles = async(files) => {
    if (!files || files.length === 0) {
      return
    }

    const maxAllowedSize = maxFileSize * 1024 * 1024

    // Single Upload -- First check
    if (singleUpload && files.length > 1) {
      setUniqueFileErrors((prevErrors) => ({ ...prevErrors, single: 'single' }))
      return
    }

    // If more files are uploaded than allowed. Default maxDropsAtOnce === 10
    if (files.length > maxDropsAtOnce) {
      setUniqueFileErrors((prevErrors) => ({ ...prevErrors, max: 'max' }))
      return
    }

    // Max and single test passed. Now checking each individual file.
    for (const file of files) {
      // Checking each file size
      if (file.size > maxAllowedSize) {
        setUniqueFileErrors((prevErrors) => ({ ...prevErrors, [file.name]: 'size' }))
      } else {
        // If all file types are accepted -> Continue setting files
        if (!acceptedFiles) {
          file.uniqueId = v4()
          setDroppedFiles((prevFiles) => [file, ...prevFiles])
        } else {
          // Go through Magic Number Check (Client)
          await validateMagicNumbers(file)
        }
      }
    }
  }

  const handleRemoveError = (index) => {
    const updatedErrors = { ...uniqueFileErrors }
    // Remove the item based on its unique identifier (index)
    delete updatedErrors[index]
    setUniqueFileErrors(updatedErrors)
  }

  return (
    <>
      {/* Dropzone Component  */}
      <div
        className={`d-flex flex-column justify-content-between bg-light-hover pointer text-center mb-2 p-4 ${isOver ? 'bg-light' : ''} ${singleUpload && droppedFiles.length === 1 ? 'd-none' : ''}`}
        style={{ border: '2px dashed #D4D4D4' }}
        onClick={() => inputRef.current?.click()}
        onMouseDown={() => setClickUpload(true)}
        onMouseOut={() => setClickUpload(false)}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
      >
        <div>
          <span
            className={`fas ${(isOver || clickUpload) ? 'fa-cloud-arrow-down text-primary' : 'fa-cloud-arrow-up text-muted'}`}
            style={{ fontSize: '5rem' }}
          />
        </div>
        <p className='fs-5 mt-3 mb-2'>
          {isOver
            ? 'Release to drop'
            : <>
              <span className='pointer'> Click to Upload or drag and drop.</span>
              {singleUpload
                ? <input type='file' id='file-uploader' style={{ display: 'none' }} onChange={(e) => handleClickUpload(e)} accept={acceptedFiles} ref={inputRef} />
                : <input type='file' id='file-uploader' style={{ display: 'none' }} onChange={(e) => handleClickUpload(e)} accept={acceptedFiles} ref={inputRef} multiple />
              }
            </>}
        </p>
        <p className='text-muted m-0'>{`Max size per file is (${maxFileSize}) ${maxFileSize === 1 ? 'MB.' : 'MBs.'}`}</p>

        {/* Descriptions */}
        {acceptedFiles && <p className='text-muted my-1'><small>({acceptedFiles.split(',')})</small></p>}
        {singleUpload && <p className='text-muted my-1'><small>Single Upload</small></p>}

      </div>

      {/* Render Unique Error Alerts */}
      <div className='my-3'>
        <div ref={containerRef} className='document-upload-alerts mb-3'>
          {Object.entries(uniqueFileErrors)
            .reverse()
            .map(([key, error], index, array) => (
              <div className={`d-flex alert alert-danger ${index === array.length - 1 ? 'mb-0' : ''}`} role='alert' key={key}>
                <div className="col-11 my-auto pe-3">
                  <div className='text-dark px-2'>
                    {error && (
                      <p className='mb-0'>
                        {error && (
                          <p className='mb-0'>
                            {!['single', 'max'].includes(error) && <>Your file <span className='fw-bold text-break'>{key}</span> could not be uploaded. </>}
                            {['single'].includes(error) && <>Your file could not be uploaded. </>}
                            {error === 'size' && <>Maximum file size cannot exceed <span className='fw-bold'>{maxFileSize}</span> {maxFileSize === 1 ? 'MB' : 'MBs'}.</>}
                            {error === 'type' && <>Only file(s) with <span className='fw-bold'>{acceptedFiles.split(',')}</span> are allowed.</>}
                            {error === 'single' && <>Maximum number of files uploaded cannot exceed <span className='fw-bold'>1</span>.</>}
                            {error === 'max' && <>Your files could not be uploaded. Maximum number of files uploaded cannot exceed <span className='fw-bold'>{maxDropsAtOnce}</span>.</>}
                          </p>
                        )}
                      </p>
                    )}
                  </div>
                </div>

                {/* Remove Unique Error Alert */}
                <div className="col-1 d-flex justify-content-end align-items-start">
                  <Button ghost size='sm' ariaLabel='Close' className='p-2 me-2' onClick={() => handleRemoveError(key)}>
                    <span className='text-dark fas fa-times fs-5' aria-hidden='true' />
                  </Button>
                </div>

              </div>
            ))}
        </div>

        {/* Passed Files now get rendered and handled with network requests */}
        {droppedFiles.map((file) => (
          <FileListItem
            key={file.uniqueId}
            file={file}
            setDroppedFiles={setDroppedFiles}
            uploadFunction={uploadFunction}
            deleteFunction={deleteFunction}
          />
        ))}
      </div>
    </>
  )
}

Dropzone.propTypes = {
  acceptedFiles: PropTypes.string,
  deleteFunction: PropTypes.func,
  isOpen: PropTypes.bool,
  maxDropsAtOnce: PropTypes.number,
  maxFileSize: PropTypes.number,
  singleUpload: PropTypes.bool,
  uploadFunction: PropTypes.func
}

export default Dropzone
